// Received media: the persistent cache first, the company's server second. A web
// app reloads often and has no filesystem, so downloads are cached in the
// database (LRU-capped) rather than re-fetched on every open.

import { contactById } from '$lib/db/contacts';
import { cachedMedia, cacheMedia } from '$lib/db/media-store';
import { downloadMedia } from '$lib/protocol/distributor-api';
import { sessionToken } from '$lib/protocol/session';

export function sniffContentType(bytes: Uint8Array): string {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    return 'image/jpeg';
  }
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e) {
    return 'image/png';
  }
  if (bytes.length >= 4 && bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
    return 'audio/ogg';
  }
  if (bytes.length >= 4 && bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
    return 'audio/webm';
  }
  if (bytes.length >= 12 && bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    return 'audio/mp4';
  }
  return 'application/octet-stream';
}

export async function mediaBytes(
  accountId: string,
  mediaId: string,
): Promise<{ bytes: Uint8Array; contentType: string } | undefined> {
  const cached = await cachedMedia(mediaId);
  if (cached !== undefined) {
    return cached;
  }
  const contact = await contactById(accountId);
  if (contact === undefined) {
    return undefined;
  }
  try {
    const token = await sessionToken(contact);
    const bytes = await downloadMedia({
      serverUrl: contact.serverUrl,
      accountId,
      token,
      mediaId,
    });
    const contentType = sniffContentType(bytes);
    await cacheMedia({ accountId, mediaId, bytes, contentType });
    return { bytes, contentType };
  } catch {
    return undefined;
  }
}
